// src/mocks/handlers/funding/commissionsHandler.ts

import { rest } from 'msw';
import commissions from '@/mocks/data/fundingCommissionsData.json';
import { API_ENDPOINTS } from '@/services/api/endpoints';
import { VITE_MSW_BASE_URL } from '@/config';
import {
  ICommission,
  TCommissionSortBy,
} from '@/domains/funding/commissions.types';
import { API_DEFAULT_PAGE_SIZE } from '@/services/api/api.constants';
import { matchesDateRange } from '@/mocks/lib/matchesDateRange';
import { PaginatedResponse } from '@/shared/types/api';
import { isAccessTokenExpired } from '@/mocks/lib/authTokenUtils';

export const fundingCommissionsHandler = rest.get<
  PaginatedResponse<ICommission>
>(
  `${VITE_MSW_BASE_URL}${API_ENDPOINTS.FUNDING_COMMISSIONS}`,
  (req, res, ctx) => {
    const token = req.headers.get('Authorization');
    if (!token) {
      return res(ctx.status(500), ctx.json({ detail: 'Something went wrong' }));
    }

    if (isAccessTokenExpired()) {
      return res(ctx.status(401), ctx.json({ error: 'Access token expired' }));
    }

    const url = new URL(req.url);
    const params = url.searchParams;
    const sourceRows = commissions as ICommission[];

    const status = params.get('statuses');
    const payment_status = params.get('payment_status');
    const currency = params.get('currencies');
    const source = params.get('sources');
    const network = params.get('networks');
    const event_date_start = params.get('event_date_start');
    const event_date_end = params.get('event_date_end');
    const approval_date_start = params.get('approval_date_start');
    const approval_date_end = params.get('approval_date_end');

    const ordering = params.get('ordering') || '';
    const page = parseInt(params.get('page') || '1', 10);
    const page_size = parseInt(
      params.get('page_size') || `${API_DEFAULT_PAGE_SIZE}`,
      10,
    );
    const search = params.get('search') || '';

    // 1. Filter
    let filtered = sourceRows.filter((item) => {
      const matchStatus = status ? item.status === status : true;
      const matchPaymentStatus = payment_status
        ? item.payment_status === payment_status
        : true;
      const matchCurrency = currency ? item.currency === currency : true;
      const matchSource = source ? item.source_name === source : true;
      const matchNetwork = network ? item.network_name === network : true;
      //range dates filters
      const matchEventDate = matchesDateRange(
        item.event_date,
        event_date_start,
        event_date_end,
      );
      const matchApprovalDate = matchesDateRange(
        item.approval_date,
        approval_date_start,
        approval_date_end,
      );

      return (
        matchStatus &&
        matchPaymentStatus &&
        matchCurrency &&
        matchSource &&
        matchNetwork &&
        matchEventDate &&
        matchApprovalDate
      );
    });

    if (search) {
      const lower = search.toLowerCase();
      filtered = filtered.filter(
        (item) =>
          item.network_name.toLowerCase().includes(lower) ||
          item.source_name?.toLowerCase().includes(lower) ||
          item.commission_id.toLowerCase().includes(lower) ||
          (item.advance_id && item.advance_id.toLowerCase().includes(lower)),
      );
    }

    // 2. Order
    if (ordering) {
      const isDesc = ordering.startsWith('-');
      const key = (isDesc ? ordering.slice(1) : ordering) as TCommissionSortBy;

      filtered = filtered.sort((a, b) => {
        const aValue = a[key];
        const bValue = b[key];

        if (aValue === null || aValue === undefined) return 1;
        if (bValue === null || bValue === undefined) return -1;

        if (typeof aValue === 'string' && typeof bValue === 'string') {
          return isDesc
            ? bValue.localeCompare(aValue)
            : aValue.localeCompare(bValue);
        }

        if (typeof aValue === 'number' && typeof bValue === 'number') {
          return isDesc ? bValue - aValue : aValue - bValue;
        }

        return 0;
      });
    }

    // 3. Paginate
    const total = filtered.length;
    const pages = Math.ceil(total / page_size);
    const start = Math.max(0, (page - 1) * page_size);
    const end = Math.min(start + page_size, total);
    const results = filtered.slice(start, end);

    // 4. Response
    return res(
      ctx.status(200),
      ctx.json({
        total,
        page,
        pages,
        results,
      }),
    );
  },
);
